<template>

  <v-container fluid class="page page-confirm-change-password">

    <v-card flat class="pt-4 registration-form-area">
      <!--------------------------------->
      <!-- Reset password confirmation -->
      <!--------------------------------->
      <template v-if="passwordChangedSuccessMessage">
        <div class="mt-3 d-flex flex-column align-center justify-start">
          <div class="main-text">
            {{ passwordChangedSuccessMessage }}
          </div>
          <v-btn
              outlined
              color="amber darken-2"
              class="mt-5"
              @click="$root.goTo('login')">
            Go to Login
          </v-btn>
        </div>
      </template>

      <!-------------------------->
      <!-- Change password form -->
      <!-------------------------->
      <v-form class="registration-form" autocomplete="off" v-else>
        <!-- title -->
        <v-row>
          <v-col align="center" class="form-title">
            {{ formTitleText }}
          </v-col>
        </v-row>

        <!-- information area -->
        <v-row class="mt-8">
          <v-col align="center" class="info-text">
            <div style="max-width:351px;">
              Please provide your email, validation code, and a new password.
            </div>
          </v-col>
        </v-row>

        <!-- email -->
        <v-row class="mt-4" align="center">
          <v-col cols="12" xs="12" sm="3" md="3" lg="3">
            <span>Email:</span>
          </v-col>
          <v-col cols="12" xs="12" sm="7" md="7" lg="7">
            <v-text-field
                outlined
                hide-details
                required
                autocomplete="email-address"
                v-model.trim="email" />
          </v-col>
        </v-row>

        <!-- validation code -->
        <v-row class="mt-4" align="center">
          <v-col cols="12" xs="12" sm="3" md="3" lg="3">
            <span>Code:</span>
          </v-col>
          <v-col cols="12" xs="12" sm="7" md="7" lg="7">
            <v-text-field
                outlined
                hide-details
                required
                autocomplete="validation-code"
                v-model.trim="validationCode" />
          </v-col>
        </v-row>

        <!-- password -->
        <v-row class="mt-4" align="start">
          <v-col cols="12" xs="12" sm="3" md="3" lg="3">
            <div class="pt-4">Password:</div>
          </v-col>
          <v-col cols="12" xs="12" sm="7" md="7" lg="7">
            <v-row>
              <v-col class="mb-0 pb-0">
                <v-text-field
                    outlined
                    required
                    v-model.trim="password"
                    autocomplete="new-password"
                    :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                    :type="showPassword ? 'text' : 'password'"
                    @click:append="showPassword = !showPassword" />
              </v-col>
            </v-row>
            <!-- password requirements -->
            <v-row class="pa-0 ma-0 mb-10">
              <v-col class="pa-0 ma-0">
                <v-tooltip right>
                  <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on" class="password-requirements-text">
                      Password requirements
                      <v-icon style="font-size:17px;" color="#34b3db" class="pl-1">mdi-information-outline</v-icon>
                    </div>
                  </template>
                  <div>
                    <span>Must be at least 8 characters, and contain:</span><br />
                    <ul class="mt-1">
                      <li>1 number</li>
                      <li>1 uppercase letter</li>
                      <li>1 lowercase letter</li>
                      <li>1 special character</li>
                    </ul>
                  </div>
                </v-tooltip>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <!-- Processing error display -->
        <template v-if="processingError">
          <transition name="fade" appear>
            <v-row class="mt-0 mb-1">
              <v-col class="d-flex align-center justify-center">
                <v-icon class="mr-2" color="#cc0000">mdi-alert-circle-outline</v-icon>
                <span class="error-message-text">{{ processingError }}</span>
                <v-btn class="ml-3" @click="clearError">OK</v-btn>
              </v-col>
            </v-row>
          </transition>
        </template>

        <!-- Update password button -->
        <v-row class="mt-0">
          <v-col class="d-flex align-center justify-center">
            <v-progress-circular indeterminate :size="40" color="primary" style="margin-top:12px;" v-if="isProcessing" />
            <v-btn
                v-else
                xlarge
                :disabled="!canChange"
                color="primary"
                height="54"
                @click="updatePassword">
              Update Password
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-card>

  </v-container>

</template>

<script>
import { Auth } from 'aws-amplify'

export default {
  name: 'ConfirmChangePasswordPage',

  data () {
    return {
      isProcessing: false,
      processingError: null,
      showPassword: false,
      passwordChangedSuccessMessage: null,
      // --------------- Input fields
      email: null,
      validationCode: null,
      password: null
    }
  },

  computed: {
    appName () {
      return this.$store.getters['app/platform']
    },
    formTitleText () {
      return 'Confirm Password Reset'
    },
    canChange () {
      return (
        this.email &&
        this.validationCode &&
        this.password
      )
    }
  },

  created () {
    // Load validation code from query parameter if exists
    const code = this.$route.query.code
    if (code) this.validationCode = code
  },

  beforeDestroy () {
    this.resetForm()
  },

  methods: {
    // Reset form
    resetForm () {
      this.processingError = null
      this.passwordChangedSuccessMessage = null
      this.email = null
      this.validationCode = null
      this.password = null
    },

    // Clear active error
    clearError () {
      this.processingError = null
    },

    // Perform confirm password reset
    async updatePassword () {
      if (!this.canChange) {
        this.processingError = 'You must provide all fields to proceed.'
        return false
      }

      this.isProcessing = true

      try {
        await Auth.forgotPasswordSubmit(this.email, this.validationCode, this.password)
        this.passwordChangedSuccessMessage = 'Your password has been changed!'
      } catch (err) {
        this.processingError = err.message || err.text || err
      } finally {
        this.isProcessing = false
      }
    }
  }
}
</script>

<style scoped>
  .page-confirm-change-password {
    margin-top: 60px;
    display: flex;
    justify-content: center;
  }

  .registration-form-area {
    padding: 24px 24px;
    background-color: #080404 !important;
    width: 500px;
    margin: 0 auto;
  }

  .form-title {
    font-size: 1.5rem;
    color: #F8A824;
    font-weight: 400;
  }

  .main-text {
    font-size: 1.16rem;
    color: #c9c9c9;
    font-weight: 400;
  }

  .info-text {
    font-size: 1.08rem;
    font-weight: 400;
  }

  .password-requirements-text {
    cursor: pointer;
    font-size: 1.0rem;
    color: #34b3db;
  }

  .error-message-text {
    font-size: 1.08rem;
    color: #cc0000;
    font-weight: 400;
  }
</style>
